import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/hooks/useCompany';
import { useAuth } from '@/contexts/AuthContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { SignedImage } from '@/components/ui/signed-image';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Building2, Users, Stethoscope, UserCheck, UserX, Clock, UserPlus, Unlink, Loader2, BarChart3, Radio, MapPin, Cake, Shield, ShieldCheck, HeartPulse, Search, CalendarDays, UsersRound, Settings as SettingsIcon } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { formatDistanceToNow, differenceInYears, parseISO } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { Navigate } from 'react-router-dom';
import { LinkPatientDialog } from '@/components/company/LinkPatientDialog';
import { InviteCompanyMemberDialog } from '@/components/company/InviteCompanyMemberDialog';
import { CompanyAnalytics } from '@/components/company/CompanyAnalytics';
import { LiveShiftPanel } from '@/components/company/LiveShiftPanel';
import { ScheduleView } from '@/components/company/ScheduleView';
import { CompanyMapView } from '@/components/company/CompanyMapView';
import { VitalAlertsPanel } from '@/components/company/VitalAlertsPanel';
import { CompanyContractBanner } from '@/components/company/CompanyContractBanner';
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist';
import { InactivePatientsCard } from '@/components/company/InactivePatientsCard';
import { Input } from '@/components/ui/input';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { toast } from 'sonner';

interface PatientShiftInfo {
  patient_id: string;
  patient_nickname: string;
  patient_photo_url: string | null;
  patient_birth_date: string | null;
  patient_address: string | null;
  patient_health_details: string | null;
  active_shift: {
    id: string;
    caregiver_id: string;
    caregiver_name: string | null;
    check_in_at: string;
  } | null;
}

export default function CompanyDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { company, isCompanyManager, isCompanyManagerOrCoordinator, isLoading: isCompanyLoading } = useCompany();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const allowedTabs = ['team', 'live', 'map', 'schedule', 'health', 'analytics'];
  const tabParam = searchParams.get('tab');
  const activeTab = tabParam && allowedTabs.includes(tabParam) ? tabParam : 'team';
  const handleTabChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'team') next.delete('tab');
      else next.set('tab', value);
      return next;
    }, { replace: true });
  };
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [unlinkingPatient, setUnlinkingPatient] = useState<{ id: string; name: string } | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [memberPage, setMemberPage] = useState(1);
  const [patientPage, setPatientPage] = useState(1);
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState('');
  const [debouncedPatientSearch, setDebouncedPatientSearch] = useState('');
  const PAGE_SIZE = 10;
  const tabsScrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll the active tab into view (centered) whenever it changes
  useEffect(() => {
    if (isCompanyLoading) return;
    const scrollToActive = () => {
      const container = tabsScrollRef.current;
      if (!container) return;
      const activeEl = container.querySelector<HTMLElement>(`[data-state="active"]`);
      if (!activeEl) return;
      const containerRect = container.getBoundingClientRect();
      const elRect = activeEl.getBoundingClientRect();
      const offset = (elRect.left + elRect.width / 2) - (containerRect.left + containerRect.width / 2);
      container.scrollBy({ left: offset, behavior: 'smooth' });
    };
    // Wait for Radix to apply data-state and layout to settle
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(scrollToActive);
    });
    return () => cancelAnimationFrame(raf);
  }, [activeTab, isCompanyLoading]);

  // Debounce search inputs (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMemberSearch(memberSearch);
      setMemberPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPatientSearch(patientSearch);
      setPatientPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [patientSearch]);

  const locale = i18n.language?.startsWith('pt') ? ptBR : enUS;

  // Fetch company patients with server-side search
  const { data: patientsWithShifts = [], isLoading: isPatientsLoading } = useQuery({
    queryKey: ['company-patients-shifts', company?.companyId, debouncedPatientSearch],
    queryFn: async (): Promise<PatientShiftInfo[]> => {
      if (!company?.companyId) return [];

      // Get all patient assignments for this company
      const { data: assignments, error: assignError } = await supabase
        .from('company_patient_assignments')
        .select('patient_id')
        .eq('company_id', company.companyId)
        .eq('is_active', true);

      if (assignError || !assignments?.length) return [];

      const patientIds = assignments.map(a => a.patient_id);

      // Fetch patient details with server-side search filter
      let patientsQuery = supabase
        .from('patients')
        .select('id, nickname, photo_url, birth_date, residence_address, address, health_details')
        .in('id', patientIds)
        .order('nickname', { ascending: true });

      if (debouncedPatientSearch) {
        const search = `%${debouncedPatientSearch}%`;
        patientsQuery = patientsQuery.or(
          `nickname.ilike.${search},residence_address.ilike.${search},address.ilike.${search},health_details.ilike.${search}`
        );
      }

      const { data: patients, error: patientError } = await patientsQuery;

      if (patientError || !patients) return [];

      // Fetch active shifts for these patients
      const filteredPatientIds = patients.map(p => p.id);
      const { data: activeShifts } = await supabase
        .from('shifts')
        .select('id, patient_id, caregiver_id, check_in_at')
        .in('patient_id', filteredPatientIds)
        .eq('status', 'active');

      // Fetch caregiver profiles for active shifts
      const caregiverIds = (activeShifts || []).map(s => s.caregiver_id);
      let caregiverProfiles: Record<string, string> = {};

      if (caregiverIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name')
          .in('user_id', caregiverIds);

        if (profiles) {
          caregiverProfiles = Object.fromEntries(
            profiles.map(p => [p.user_id, p.full_name || ''])
          );
        }
      }

      // Map patients with their shift info
      return patients.map(patient => {
        const shift = (activeShifts || []).find(s => s.patient_id === patient.id);
        return {
          patient_id: patient.id,
          patient_nickname: patient.nickname,
          patient_photo_url: patient.photo_url,
          patient_birth_date: patient.birth_date,
          patient_address: patient.residence_address || patient.address,
          patient_health_details: patient.health_details,
          active_shift: shift ? {
            id: shift.id,
            caregiver_id: shift.caregiver_id,
            caregiver_name: caregiverProfiles[shift.caregiver_id] || null,
            check_in_at: shift.check_in_at,
          } : null,
        };
      });
    },
    enabled: !!company?.companyId,
    // Realtime listener below invalidates on shift changes; slow safety poll.
    refetchInterval: 120000,
    refetchOnWindowFocus: false,
    staleTime: 60000,
  });

  // Realtime: invalidate the company patients/shifts list when any shift row changes.
  useEffect(() => {
    if (!company?.companyId) return;
    const channel = supabase
      .channel(`company-active-shifts-${company.companyId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'shifts' },
        () => {
          queryClient.invalidateQueries({
            queryKey: ['company-patients-shifts', company.companyId],
          });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [company?.companyId, queryClient]);

  // Fetch company members with server-side search
  const { data: companyMembers = [], isLoading: isMembersLoading } = useQuery({
    queryKey: ['company-members-list', company?.companyId, debouncedMemberSearch],
    queryFn: async () => {
      if (!company?.companyId) return [];

      const { data: members, error } = await supabase
        .from('company_members')
        .select('user_id, role')
        .eq('company_id', company.companyId)
        .eq('is_active', true);

      if (error || !members?.length) return [];

      const userIds = members.map(m => m.user_id);

      // Fetch profiles with server-side search
      let profilesQuery = supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url')
        .in('user_id', userIds)
        .order('full_name', { ascending: true });

      if (debouncedMemberSearch) {
        profilesQuery = profilesQuery.ilike('full_name', `%${debouncedMemberSearch}%`);
      }

      const { data: profiles } = await profilesQuery;

      const profileMap = Object.fromEntries(
        (profiles || []).map(p => [p.user_id, p])
      );

      // If searching, only return members that matched the profile search
      const filteredMembers = debouncedMemberSearch
        ? members.filter(m => profileMap[m.user_id])
        : members;

      return filteredMembers.map(m => ({
        user_id: m.user_id,
        role: m.role as string,
        full_name: profileMap[m.user_id]?.full_name || null,
        avatar_url: profileMap[m.user_id]?.avatar_url || null,
      })).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
    },
    enabled: !!company?.companyId,
  });

  // Pagination computed values
  const memberTotalPages = Math.ceil(companyMembers.length / PAGE_SIZE);
  const paginatedMembers = companyMembers.slice((memberPage - 1) * PAGE_SIZE, memberPage * PAGE_SIZE);

  const patientTotalPages = Math.ceil(patientsWithShifts.length / PAGE_SIZE);
  const paginatedPatients = patientsWithShifts.slice((patientPage - 1) * PAGE_SIZE, patientPage * PAGE_SIZE);

  if (isCompanyLoading) {
    return (
      <AppLayout title={t('company.dashboard')}>
        <div className="space-y-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </AppLayout>
    );
  }

  if (!isCompanyManagerOrCoordinator) {
    return <Navigate to="/app" replace />;
  }

  const handleUnlink = async () => {
    if (!unlinkingPatient || !company?.companyId) return;
    setIsUnlinking(true);
    try {
      const { error } = await supabase
        .from('company_patient_assignments')
        .update({ is_active: false })
        .eq('company_id', company.companyId)
        .eq('patient_id', unlinkingPatient.id);

      if (error) throw error;

      toast.success(t('company.unlinkSuccess'));
      await queryClient.invalidateQueries({ queryKey: ['company-patients-shifts'] });
      await queryClient.invalidateQueries({ queryKey: ['manager-available-patients'] });
    } catch (error) {
      console.error('Error unlinking patient:', error);
      toast.error(t('company.unlinkError'));
    } finally {
      setIsUnlinking(false);
      setUnlinkingPatient(null);
    }
  };

  const activeShiftCount = patientsWithShifts.filter(p => p.active_shift).length;
  const unattendedCount = patientsWithShifts.filter(p => !p.active_shift).length;

  return (
    <AppLayout title={t('company.dashboard')} showBack>
      <div className="space-y-4 p-4">
        {/* Company Header */}
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center overflow-hidden shrink-0">
            {company?.logoUrl ? (
              <SignedImage
                src={company.logoUrl}
                bucket="company-logos"
                className="h-full w-full object-cover"
                alt={company.companyName}
                fallback={<Building2 className="h-6 w-6 text-primary" />}
              />
            ) : (
              <Building2 className="h-6 w-6 text-primary" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold truncate">{company?.companyName}</h2>
            <p className="text-sm text-muted-foreground">{t('company.dashboard')}</p>
          </div>
          {isCompanyManager && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/app/company/settings')}
              className="shrink-0"
            >
              <SettingsIcon className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">{t('company.settingsTitle')}</span>
            </Button>
          )}
        </div>

        {/* Contract acceptance banner (managers only, when pending) */}
        <CompanyContractBanner />

        {/* Activation checklist (only while required steps are pending) */}
        <OnboardingChecklist requiredOnly />


        {/* Tabs: Team + Analytics */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <div ref={tabsScrollRef} className="overflow-x-auto -mx-4 px-4 scrollbar-none">
            <TabsList className="w-max min-w-full">
              <TabsTrigger value="team" className="gap-1.5 whitespace-nowrap">
                <Users className="h-4 w-4" />
                {t('analytics.teamTab')}
              </TabsTrigger>
              <TabsTrigger value="live" className="gap-1.5 whitespace-nowrap">
                <Radio className="h-4 w-4" />
                <span className="relative">
                  {t('live.tab')}
                  <span className="absolute -top-0.5 -right-2 h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
                </span>
              </TabsTrigger>
              <TabsTrigger value="map" className="gap-1.5 whitespace-nowrap">
                <MapPin className="h-4 w-4" />
                {t('company.mapTab')}
              </TabsTrigger>
              <TabsTrigger value="schedule" className="gap-1.5 whitespace-nowrap">
                <CalendarDays className="h-4 w-4" />
                {t('schedule.tab')}
              </TabsTrigger>
              <TabsTrigger value="health" className="gap-1.5 whitespace-nowrap">
                <HeartPulse className="h-4 w-4" />
                {t('health.tab')}
              </TabsTrigger>
              <TabsTrigger value="analytics" className="gap-1.5 whitespace-nowrap">
                <BarChart3 className="h-4 w-4" />
                {t('analytics.tab')}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="team" className="space-y-4 mt-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-3">
              <Card>
                <CardContent className="p-4 text-center">
                  <Users className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-2xl font-bold">{patientsWithShifts.length}</p>
                  <p className="text-xs text-muted-foreground">{t('company.totalPatients')}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                   <UserCheck className="h-5 w-5 mx-auto mb-1 text-success" />
                   <p className="text-2xl font-bold text-success">{activeShiftCount}</p>
                  <p className="text-xs text-muted-foreground">{t('company.activeShifts')}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                   <UserX className="h-5 w-5 mx-auto mb-1 text-warning" />
                   <p className="text-2xl font-bold text-warning">{unattendedCount}</p>
                  <p className="text-xs text-muted-foreground">{t('company.noShift')}</p>
                </CardContent>
              </Card>
            </div>

            {/* Inactive patients hygiene card (managers/coordinators) */}
            {isCompanyManagerOrCoordinator && company?.companyId && (
              <InactivePatientsCard
                companyId={company.companyId}
                patientIds={patientsWithShifts.map(p => p.patient_id)}
              />
            )}

            {/* Patient Shift List */}

            <div>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-info/10 rounded-lg shrink-0">
                    <HeartPulse className="h-5 w-5 text-info" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-base font-semibold text-foreground">
                        {t('company.patientsSectionTitle')}
                      </h2>
                      <Badge variant="secondary">{patientsWithShifts.length}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {t('company.patientsSectionSubtitle')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 md:flex-none">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder={t('company.searchPatients')}
                      value={patientSearch}
                      onChange={(e) => setPatientSearch(e.target.value)}
                      className="h-8 pl-8 w-full md:w-64"
                    />
                  </div>
                  {isCompanyManager && (
                    <Button size="sm" variant="outline" onClick={() => setLinkDialogOpen(true)} className="shrink-0 whitespace-normal text-xs h-auto py-1.5 px-2.5 max-w-[130px] text-center leading-tight">
                      <UserPlus className="h-3.5 w-3.5 shrink-0" />
                      {t('company.linkPatient')}
                    </Button>
                  )}
                </div>
              </div>

              {isPatientsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
                </div>
              ) : paginatedPatients.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>{patientSearch ? t('common.noResults') : t('company.noPatients')}</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {paginatedPatients.map((item) => (
                    <Card
                      key={item.patient_id}
                      className="cursor-pointer hover:border-primary/30 transition-colors"
                      onClick={() => navigate(`/app/patients/${item.patient_id}`)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <SignedAvatar
                            src={item.patient_photo_url}
                            bucket="patient-photos"
                            className="h-10 w-10"
                            fallback={
                              <span className="bg-primary/10 text-primary text-sm flex items-center justify-center w-full h-full">
                                {item.patient_nickname.charAt(0).toUpperCase()}
                              </span>
                            }
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{item.patient_nickname}</p>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                              {item.patient_birth_date && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Cake className="h-3 w-3" />
                                  {differenceInYears(new Date(), parseISO(item.patient_birth_date))} {t('company.yearsOld')}
                                </span>
                              )}
                              {item.patient_address && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1 truncate max-w-[200px]">
                                  <MapPin className="h-3 w-3 shrink-0" />
                                  {item.patient_address}
                                </span>
                              )}
                            </div>
                            {item.patient_health_details && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2 flex items-start gap-1">
                                <HeartPulse className="h-3 w-3 shrink-0 mt-0.5" />
                                {item.patient_health_details}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5">
                            {isCompanyManager && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUnlinkingPatient({ id: item.patient_id, name: item.patient_nickname });
                                }}
                              >
                                <Unlink className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {patientTotalPages > 1 && (
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => setPatientPage(p => Math.max(1, p - 1))}
                            className={patientPage <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          >{t('common.back')}</PaginationPrevious>
                        </PaginationItem>
                        {Array.from({ length: patientTotalPages }, (_, i) => i + 1).map(page => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              isActive={page === patientPage}
                              onClick={() => setPatientPage(page)}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => setPatientPage(p => Math.min(patientTotalPages, p + 1))}
                            className={patientPage >= patientTotalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          >{t('common.next')}</PaginationNext>
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  )}
                </div>
              )}
            </div>

            <Separator className="my-2" />

            {/* Caregivers List */}
            <div>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                    <UsersRound className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-base font-semibold text-foreground">
                        {t('company.teamSectionTitle')}
                      </h2>
                      <Badge variant="secondary">{companyMembers.length}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {t('company.teamSectionSubtitle')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 md:flex-none">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder={t('company.searchMembers')}
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      className="h-8 pl-8 w-full md:w-64"
                    />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setInviteDialogOpen(true)} className="shrink-0 whitespace-normal text-xs h-auto py-1.5 px-2.5 max-w-[130px] text-center leading-tight">
                    <UserPlus className="h-3.5 w-3.5 shrink-0" />
                    {t('company.inviteMembers')}
                  </Button>
                </div>
              </div>

              {isMembersLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : paginatedMembers.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    <Stethoscope className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>{memberSearch ? t('common.noResults') : t('company.noCaregivers')}</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {paginatedMembers.map((member) => (
                    <Card key={member.user_id}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <SignedAvatar
                            src={member.avatar_url}
                            bucket="avatars"
                            className="h-10 w-10"
                            fallback={
                              <span className="bg-primary/10 text-primary text-sm flex items-center justify-center w-full h-full">
                                {(member.full_name || '?').charAt(0).toUpperCase()}
                              </span>
                            }
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{member.full_name || t('common.unnamed')}</p>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              {member.role === 'manager' ? (
                                <><ShieldCheck className="h-3 w-3" />{t('company.roleManager')}</>
                              ) : member.role === 'coordinator' ? (
                                <><Shield className="h-3 w-3" />{t('company.roleCoordinator')}</>
                              ) : (
                                <><Stethoscope className="h-3 w-3" />{t('company.roleEmployee')}</>
                              )}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {memberTotalPages > 1 && (
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => setMemberPage(p => Math.max(1, p - 1))}
                            className={memberPage <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          >{t('common.back')}</PaginationPrevious>
                        </PaginationItem>
                        {Array.from({ length: memberTotalPages }, (_, i) => i + 1).map(page => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              isActive={page === memberPage}
                              onClick={() => setMemberPage(page)}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => setMemberPage(p => Math.min(memberTotalPages, p + 1))}
                            className={memberPage >= memberTotalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          >{t('common.next')}</PaginationNext>
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="live" className="mt-4">
            {company?.companyId && <LiveShiftPanel companyId={company.companyId} />}
          </TabsContent>

          <TabsContent value="map" className="mt-4">
            {company?.companyId && <CompanyMapView companyId={company.companyId} />}
          </TabsContent>

          <TabsContent value="schedule" className="mt-4">
            {company?.companyId && <ScheduleView companyId={company.companyId} />}
          </TabsContent>

          <TabsContent value="health" className="mt-4">
            {company?.companyId && <VitalAlertsPanel companyId={company.companyId} />}
          </TabsContent>

          <TabsContent value="analytics" className="mt-4">
            {company?.companyId && <CompanyAnalytics companyId={company.companyId} />}
          </TabsContent>
        </Tabs>

        {/* Link Patient Dialog */}
        {company?.companyId && (
          <>
            <LinkPatientDialog
              open={linkDialogOpen}
              onOpenChange={setLinkDialogOpen}
              companyId={company.companyId}
              alreadyLinkedPatientIds={patientsWithShifts.map(p => p.patient_id)}
            />
            <InviteCompanyMemberDialog
              open={inviteDialogOpen}
              onOpenChange={setInviteDialogOpen}
              companyId={company.companyId}
              companyName={company.companyName}
              canInviteCoordinators={isCompanyManager}
            />
          </>
        )}

        {/* Unlink Confirmation Dialog */}
        <AlertDialog open={!!unlinkingPatient} onOpenChange={(open) => !open && setUnlinkingPatient(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('company.unlinkPatient')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('company.unlinkConfirm', { name: unlinkingPatient?.name })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isUnlinking}>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleUnlink}
                disabled={isUnlinking}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isUnlinking ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {t('company.unlinkPatient')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppLayout>
  );
}
